import { useEffect, useState } from 'react';
import {
  Box,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { documentRequestsApi, fromAxiosError } from '@/core/api';
import type { DocumentRequestListItem } from '@/core/api';
import {
  DEFAULT_PAGE_SIZE,
  EmptyState,
  PaginationFooter,
  SortableHeaderCell,
  StatusChip,
  nextSortDir,
  useDebouncedValue,
  useFormattedDate,
} from '@/shared';
import type { PageSizeOption } from '@/shared';
import { relativeTime } from '@/core/utils';

/** Columns the API knows how to order by. */
type SortColumn = 'claimNumber' | 'customer' | 'documentType' | 'status' | 'requested';

export interface DocumentRequestsTableProps {
  /** Server-side status filter, e.g. 'PENDING'. Omit for every status. */
  status?: string;
  /**
   * How many rows to fetch when `paginated` is false — the dashboard panel
   * wants a short peek, not a page. Ignored when paginated, where the size is
   * the user's choice from PAGE_SIZE_OPTIONS.
   */
  rowLimit?: number;
  /** Shown when the query comes back empty. */
  emptyTitle: string;
  emptyDescription?: string;
  /** Hidden for customers, who already know which company they are. */
  showCustomer?: boolean;
  /**
   * Full controls. Off for the dashboard panel, which is a peek at the top of
   * the list — a search box and pager inside a summary card would be a second
   * competing list on a page that already has a "View all" link.
   */
  paginated?: boolean;
}

/**
 * A list of document requests.
 *
 * Shared by the dashboard panel and the full page rather than written twice.
 * The two differ only in whether the controls are on and how many rows they
 * ask for, which are props.
 *
 * Search, sort and paging are all server-side. Doing any of them in the browser
 * would silently mean "search the current page", which looks like search until
 * the day a customer has more documents than fit on one — and a customer with
 * 88 outstanding documents is exactly who needs it.
 *
 * Rows link to the claim, not to a document detail page: there is no such page,
 * and the claim's Documents tab is where a document is actually uploaded,
 * approved or rejected. Sending someone to a dead end would be worse than the
 * extra click.
 */
export default function DocumentRequestsTable({
  status,
  rowLimit = 25,
  emptyTitle,
  emptyDescription,
  showCustomer = false,
  paginated = false,
}: DocumentRequestsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatLocalized = useFormattedDate();

  const [rows, setRows] = useState<DocumentRequestListItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [orderBy, setOrderBy] = useState<SortColumn>('requested');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  /** 1-indexed for the footer; converted to the API's 0-indexed page below. */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(DEFAULT_PAGE_SIZE);
  // Unpaginated callers get exactly the rows they asked for, on one page.
  const effectivePageSize = paginated ? pageSize : rowLimit;

  // Any change to what is being asked for invalidates the current page number —
  // staying on page 4 of a freshly-filtered list shows an empty table.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, orderBy, direction, effectivePageSize, status]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorMessage(null);

    documentRequestsApi
      .getDocumentRequests({
        status,
        search: debouncedSearch.trim() || undefined,
        orderBy,
        orderDirection: direction,
        page: page - 1,
        pageSize: effectivePageSize,
      })
      .then((r) => {
        if (cancelled) return;
        setRows(r.elements ?? []);
        setTotalPages(Math.max(1, r.totalPage ?? 1));
      })
      .catch((err) => {
        if (cancelled) return;
        setRows([]);
        setErrorMessage(fromAxiosError(err).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [status, debouncedSearch, orderBy, direction, page, effectivePageSize]);

  function handleSort(column: SortColumn) {
    setDirection(nextSortDir(column, orderBy, direction));
    setOrderBy(column);
  }

  const ns = 'DOCUMENT-REQUESTS';

  // A search that matched nothing is not the same as having nothing — telling
  // someone "you owe no documents" when they mistyped a claim number would be
  // a lie they might act on.
  const isFiltered = debouncedSearch.trim() !== '';

  const body = (() => {
    if (loading) {
      return (
        <Box>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={40} />
          ))}
        </Box>
      );
    }
    if (errorMessage !== null) return <EmptyState title={errorMessage} />;
    if (rows.length === 0) {
      return isFiltered ? (
        <EmptyState title={t(`${ns}.noMatches`, { term: debouncedSearch.trim() })} />
      ) : (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      );
    }

    return (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableHeaderCell
                column="claimNumber"
                current={orderBy}
                direction={direction}
                onSort={handleSort}
              >
                {t(`${ns}.columns.claimNumber`)}
              </SortableHeaderCell>
              {showCustomer && (
                <SortableHeaderCell
                  column="customer"
                  current={orderBy}
                  direction={direction}
                  onSort={handleSort}
                >
                  {t(`${ns}.columns.customer`)}
                </SortableHeaderCell>
              )}
              <SortableHeaderCell
                column="documentType"
                current={orderBy}
                direction={direction}
                onSort={handleSort}
              >
                {t(`${ns}.columns.documentType`)}
              </SortableHeaderCell>
              <SortableHeaderCell
                column="status"
                current={orderBy}
                direction={direction}
                onSort={handleSort}
              >
                {t(`${ns}.columns.status`)}
              </SortableHeaderCell>
              <SortableHeaderCell
                column="requested"
                current={orderBy}
                direction={direction}
                onSort={handleSort}
                align="right"
              >
                {t(`${ns}.columns.requested`)}
              </SortableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.documentId}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/claims/${row.claimId}`)}
              >
                <TableCell sx={{ fontWeight: 600 }}>{row.claimNumber || '—'}</TableCell>
                {showCustomer && <TableCell>{row.customerName || '—'}</TableCell>}
                <TableCell>
                  {row.documentType || '—'}
                  {row.mandatory && ' *'}
                </TableCell>
                <TableCell>
                  <StatusChip
                    status={row.status}
                    label={t(`${ns}.status.${row.status}`, { defaultValue: row.status })}
                  />
                </TableCell>
                <TableCell align="right">
                  {/* Falls back to when the slot was created: an OPEN row has
                      never been requested, so requestDate is null by design. */}
                  <Tooltip title={formatLocalized(row.requestDate ?? row.creationDate)}>
                    <span>{relativeTime(row.requestDate ?? row.creationDate) ?? '—'}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  })();

  if (!paginated) return body;

  return (
    <Box>
      <TextField
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t(`${ns}.searchPlaceholder`)}
        aria-label={t(`${ns}.searchPlaceholder`)}
        sx={{ mb: 2, width: { xs: '100%', sm: 340 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {body}
      <PaginationFooter
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        loading={loading}
        pageSizeId="document-requests"
      />
    </Box>
  );
}
